import React, { useState } from 'react';
import { ArrowLeft, Copy, ExternalLink, Play, Loader2 } from 'lucide-react';
import { Sidebar } from '../components/Sidebar';
import { Alert } from '../types';
import { formatTimestamp } from '../utils/helpers';

// Simple markdown renderer for formatting text
const renderMarkdown = (text: any) => {
  if (!text || typeof text !== 'string') return null;

  const lines = text.split('\n');
  const elements: JSX.Element[] = [];

  lines.forEach((line, index) => {
    // Bold text: **text** -> <strong>text</strong>
    const boldFormatted = line.replace(/\*\*(.+?)\*\*/g, '<strong class="font-semibold text-white">$1</strong>');

    // Numbered list: "1. text" or "2. text"
    if (/^\d+\.\s/.test(line)) {
      elements.push(
        <li key={index} className="ml-4" dangerouslySetInnerHTML={{ __html: boldFormatted.replace(/^\d+\.\s/, '') }} />
      );
    }
    // Bullet list: "- text"
    else if (/^-\s/.test(line)) {
      elements.push(
        <li key={index} className="ml-4" dangerouslySetInnerHTML={{ __html: boldFormatted.replace(/^-\s/, '') }} />
      );
    }
    // Regular paragraph
    else if (line.trim()) {
      elements.push(
        <p key={index} className="mb-2" dangerouslySetInnerHTML={{ __html: boldFormatted }} />
      );
    }
    // Empty line
    else {
      elements.push(<br key={index} />);
    }
  });

  return <>{elements}</>;
};

interface AlertDetailsPageProps {
  currentPage: string;
  activeAlertsCount: number;
  selectedAlert: Alert;
  alertAnalysis: any;
  analysisVersions: any[];
  selectedAnalysisVersion: number;
  servicesConfig: any[];
  onNavigate: (page: string) => void;
  onLogout: () => void;
  onGoBackToAlerts: () => void;
  onAnalysisVersionChange: (versionIndex: number) => void;
  onTriggerAnalysis: () => Promise<void>;
}


export const AlertDetailsPage: React.FC<AlertDetailsPageProps> = ({
  currentPage,
  activeAlertsCount,
  selectedAlert,
  alertAnalysis,
  analysisVersions = [],
  selectedAnalysisVersion = 0,
  servicesConfig = [],
  onNavigate,
  onLogout,
  onGoBackToAlerts,
  onAnalysisVersionChange,
  onTriggerAnalysis,
}) => {
  const [linkCopied, setLinkCopied] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  // Helper to parse analysis_result if it's a JSON string
  const parseAnalysisResult = (analysisResult: any) => {
    if (!analysisResult) return null;
    if (typeof analysisResult === 'string') {
      try {
        return JSON.parse(analysisResult);
      } catch (e) {
        console.error('Failed to parse analysis_result:', e);
        return null;
      }
    }
    return analysisResult;
  };

  // Check if service is configured
  const isServiceConfigured = servicesConfig.some(
    (config: any) => {
      return config.service_name === selectedAlert.service_name;
    }
  );

  // Chain of thought steps
  const analysisSteps = [
    { text: 'Analyzing alert context...', duration: 2000 },
    { text: 'Fetching service configuration...', duration: 1500 },
    { text: 'Querying Grafana dashboards...', duration: 2000 },
    { text: 'Retrieving recent commits from GitHub...', duration: 1800 },
    { text: 'Searching similar past incidents...', duration: 2200 },
    { text: 'Analyzing metrics and logs...', duration: 2500 },
    { text: 'Generating root cause analysis...', duration: 3000 },
  ];

  const handleBackClick = () => {
    onGoBackToAlerts();
  };

  const handleCopyLink = async () => {
    try {
      // Construct deep link with alert parameter
      const baseUrl = window.location.origin + window.location.pathname;
      const deepLink = `${baseUrl}?alert=${selectedAlert.id}`;
      await navigator.clipboard.writeText(deepLink);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  const getSeverityColor = (priority: number, isResolved: boolean) => {
    if (isResolved) return 'bg-green-500';
    if (priority >= 4) return 'bg-red-500';
    if (priority >= 3) return 'bg-orange-500';
    if (priority >= 2) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const handleTriggerAnalysis = async () => {
    setIsAnalyzing(true);
    setCurrentStep(0);

    // Start the chain of thought animation
    const animateSteps = () => {
      let totalDuration = 0;
      analysisSteps.forEach((step, index) => {
        setTimeout(() => {
          setCurrentStep(index + 1);
        }, totalDuration);
        totalDuration += step.duration;
      });
    };

    animateSteps();

    try {
      // Trigger the actual analysis - waits for completion
      await onTriggerAnalysis();
    } finally {
      // Reset after analysis completes
      setIsAnalyzing(false);
      setCurrentStep(0);
    }
  };

  return (
    <div className="h-screen bg-[#1A1A1A] text-white flex overflow-hidden">
      <Sidebar
        currentPage={currentPage}
        activeAlertsCount={activeAlertsCount}
        onNavigate={onNavigate}
        onLogout={onLogout}
      />
      <div className="flex-1 ml-64 p-8 overflow-y-auto">
        <button
          onClick={handleBackClick}
          className="flex items-center space-x-2 text-[#8b949e] hover:bg-[#1A1A1A] hover:text-gray-200 mb-4 px-3 py-2 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Back to Alerts</span>
        </button>

        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-3">
              <div className={`w-4 h-4 rounded-full ${getSeverityColor(selectedAlert.priority, selectedAlert.status === 'RESOLVED')}`}></div>
              <h1 className="text-3xl font-bold text-white">{selectedAlert.title}</h1>
              {selectedAlert.status === 'RESOLVED' && (
                <span className="bg-green-600 px-3 py-1 rounded-full text-sm">RESOLVED</span>
              )}
            </div>
            <button
              onClick={handleCopyLink}
              className="flex items-center space-x-2 bg-[#222222] hover:bg-[#333333] px-4 py-2 rounded-lg transition-colors"
            >
              <Copy className="w-4 h-4" />
              <span>{linkCopied ? 'Copied!' : 'Copy Link'}</span>
            </button>
          </div>
          <div className="text-[#8b949e] text-sm space-y-1">
            <div>Alert ID: {selectedAlert.id}</div>
            <div>Service: {selectedAlert.service_name}</div>
          </div>
        </div>

        <div className="space-y-6">
          {/* Service Config Warning */}
          {!isServiceConfigured && (
            <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <div className="flex-1">
                  <h4 className="text-yellow-400 font-semibold mb-1">Limited Analysis - Service Not Configured</h4>
                  <p className="text-yellow-200/80 text-sm mb-2">
                    Root cause analysis is limited.
                    Please add <span className="font-semibold">{selectedAlert.service_name}</span> to <b>Service Config</b> in Settings.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Root Cause Analysis */}
          <div className="bg-[#222222] rounded-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-white">Root Cause Analysis</h2>
              <div className="flex items-center gap-3">
                {analysisVersions && analysisVersions.length > 0 && (
                  <div className="flex items-center gap-2">
                    <label className="text-sm text-[#8b949e]">Version:</label>
                    <select
                      value={selectedAnalysisVersion}
                      onChange={(e) => onAnalysisVersionChange(Number(e.target.value))}
                      className="bg-[#1A1A1A] border border-gray-600 rounded px-3 py-1 text-white text-sm"
                    >
                      {analysisVersions
                        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
                        .map((analysis, index) => {
                          const maxVersion = Math.max(...analysisVersions.map(a => a.version));
                          const statusIndicator = analysis.status === 'COMPLETED' ? '✓' : analysis.status === 'FAILED' ? '✗' : '○';
                          return (
                            <option key={analysis.id} value={index}>
                              Version {analysis.version}/{maxVersion} {statusIndicator} ({new Date(analysis.created_at).toLocaleDateString()})
                            </option>
                          );
                        })}
                    </select>
                  </div>
                )}
                <button
                  onClick={handleTriggerAnalysis}
                  disabled={isAnalyzing}
                  className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed px-4 py-2 rounded-lg text-sm font-semibold transition-colors"
                >
                  {isAnalyzing ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Analyzing...
                    </>
                  ) : (
                    <>
                      <Play className="w-4 h-4" />
                      Trigger Analysis
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Chain of Thought Loading */}
            {isAnalyzing && (
              <div className="bg-[#1A1A1A] border border-blue-500/30 rounded-lg p-6 mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />
                  <h3 className="text-lg font-semibold text-white">Running Analysis...</h3>
                </div>
                <div className="space-y-3">
                  {analysisSteps.map((step, index) => (
                    <div key={index} className="flex items-center gap-3">
                      {index < currentStep ? (
                        <div className="w-5 h-5 rounded-full bg-green-600 flex items-center justify-center flex-shrink-0">
                          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      ) : index === currentStep ? (
                        <Loader2 className="w-5 h-5 text-blue-500 animate-spin flex-shrink-0" />
                      ) : (
                        <div className="w-5 h-5 rounded-full border-2 border-gray-600 flex-shrink-0"></div>
                      )}
                      <span className={`text-sm ${
                        index < currentStep ? 'text-gray-400 line-through' :
                        index === currentStep ? 'text-white font-medium' :
                        'text-gray-500'
                      }`}>
                        {step.text}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Hide existing analysis while new one is running */}
            {!isAnalyzing && alertAnalysis ? (
              <div className="space-y-6">
                {/* Analysis Status and Confidence */}
                <div className="flex items-center gap-4">
                  <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                    alertAnalysis.status === 'COMPLETED' ? 'bg-green-600 text-white' :
                    alertAnalysis.status === 'PENDING' ? 'bg-yellow-600 text-white' :
                    'bg-red-600 text-white'
                  }`}>
                    {alertAnalysis.status?.toUpperCase() || 'LOADING'}
                  </span>
                  {alertAnalysis.confidence_score !== null && alertAnalysis.confidence_score !== undefined && (
                    <span className="text-sm text-[#8b949e]">
                      Confidence Score: {Math.round(alertAnalysis.confidence_score * 100)}%
                    </span>
                  )}
                  {alertAnalysis.analysis_time_ms && (
                    <span className="text-sm text-[#8b949e]">
                      Analysis Time: {(alertAnalysis.analysis_time_ms / 1000).toFixed(1)}s
                    </span>
                  )}
                </div>

                {/* Show error message for failed analyses */}
                {alertAnalysis.status === 'FAILED' && alertAnalysis.error_message && (
                  <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-4">
                    <h4 className="text-red-400 font-semibold mb-2">Analysis Failed</h4>
                    <p className="text-red-300 text-sm">{alertAnalysis.error_message}</p>
                  </div>
                )}

                {/* Analysis Result - only show for completed analyses */}
                {alertAnalysis.status === 'COMPLETED' && alertAnalysis.analysis_result && (
                  <div className="space-y-6">
                    {(() => {
                      // Parse analysis_result if it's a JSON string
                      const parsedAnalysisResult = parseAnalysisResult(alertAnalysis.analysis_result);

                      // Check if we have investigation data
                      const investigation = parsedAnalysisResult?.investigation;
                      const hasInvestigation = investigation !== null && investigation !== undefined;

                      return (
                        <>
                          {/* Root Cause Summary */}
                          {hasInvestigation && investigation.summary && (
                            <div className="bg-white/5 border border-white/10 rounded-lg p-6">
                              <h3 className="text-lg font-semibold mb-4 text-white">Root Cause Summary</h3>
                              <div className="text-gray-300">
                                <p className="mb-2">{investigation.summary}</p>
                                {investigation.root_cause && investigation.root_cause !== investigation.summary && (
                                  <p className="mt-2 text-sm text-gray-400">
                                    <span className="font-semibold">Root Cause:</span> {investigation.root_cause}
                                  </p>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Fallback: Old format root cause summary */}
                          {!hasInvestigation && parsedAnalysisResult?.root_cause_summary && (
                            <div className="bg-white/5 border border-white/10 rounded-lg p-6">
                              <h3 className="text-lg font-semibold mb-4 text-white">Root Cause Summary</h3>
                              <div className="text-gray-300">
                                {renderMarkdown(parsedAnalysisResult.root_cause_summary)}
                              </div>
                            </div>
                          )}

                          {/* Timeline */}
                          {hasInvestigation && investigation.timeline && Array.isArray(investigation.timeline) && investigation.timeline.length > 0 && (
                            <div className="bg-white/5 border border-white/10 rounded-lg p-6">
                              <h3 className="text-lg font-semibold mb-4 text-white">Timeline</h3>
                              <div className="space-y-4">
                                {investigation.timeline.map((event: any, index: number) => (
                                  <div key={index} className="flex items-start gap-4">
                                    <div className="flex-shrink-0">
                                      <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm font-semibold">
                                        {index + 1}
                                      </div>
                                    </div>
                                    <div className="flex-1">
                                      <div className="flex items-center justify-between mb-1">
                                        <h4 className="text-white font-semibold">{event.event}</h4>
                                        <div className="flex items-center gap-2">
                                          {event.source && (
                                            <span className="px-2 py-1 rounded text-xs font-medium bg-gray-700 text-gray-300">
                                              {event.source}
                                            </span>
                                          )}
                                          {event.confidence && (
                                            <span className="px-2 py-1 rounded text-xs font-medium bg-blue-700 text-blue-200">
                                              {(event.confidence * 100).toFixed(0)}% confidence
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                      <div className="text-[#8b949e] text-sm mb-2">
                                        {(() => {
                                          // Handle time ranges with " to " format
                                          if (event.time && typeof event.time === 'string' && event.time.includes(' to ')) {
                                            const parts = event.time.split(' to ');
                                            const startTime = formatTimestamp(parts[0].trim());
                                            const endTime = formatTimestamp(parts[1].trim());
                                            return `${startTime} to ${endTime}`;
                                          }
                                          return formatTimestamp(event.time);
                                        })()}
                                      </div>
                                      {event.details && (
                                        <p className="text-gray-300 text-sm">{event.details}</p>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Fallback: Old format timeline */}
                          {!hasInvestigation && parsedAnalysisResult?.timeline && (
                            <div className="bg-white/5 border border-white/10 rounded-lg p-6">
                              <h3 className="text-lg font-semibold mb-4 text-white">Timeline</h3>
                              <div className="space-y-4">
                                {(() => {
                                  const timeline = parsedAnalysisResult.timeline;

                                  // If timeline has structured events, use them
                                  if (timeline.events && Array.isArray(timeline.events)) {
                                    return timeline.events.map((event: any, index: number) => (
                                      <div key={index} className="flex items-start gap-4">
                                        <div className="flex-shrink-0">
                                          <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm font-semibold">
                                            {index + 1}
                                          </div>
                                        </div>
                                        <div className="flex-1">
                                          <div className="flex items-center justify-between mb-1">
                                            <h4 className="text-white font-semibold">{event.event}</h4>
                                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                                              event.severity === 'critical' ? 'bg-red-600 text-white' :
                                              event.severity === 'high' ? 'bg-orange-600 text-white' :
                                              event.severity === 'medium' ? 'bg-yellow-600 text-white' :
                                              'bg-green-600 text-white'
                                            }`}>
                                              {event.severity}
                                            </span>
                                          </div>
                                          <div className="text-[#8b949e] text-sm mb-2">{formatTimestamp(event.timestamp)}</div>
                                          <p className="text-gray-300 text-sm">{event.description}</p>
                                          {event.metrics && (
                                            <div className="mt-2 text-xs text-[#8b949e]">
                                              <span className="font-semibold">Metrics:</span> {JSON.stringify(event.metrics)}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    ));
                                  }

                                  // If timeline is text, parse it for timestamp patterns
                                  if (typeof timeline === 'string') {
                                    const lines = timeline.split('\n');
                                    const events: Array<{time: string, description: string, severity: string}> = [];

                                    lines.forEach(line => {
                                      const trimmed = line.trim();
                                      // Match patterns like "2025-10-23T15:30:00Z:", "Before incident:", "Incident Start:", etc.
                                      const timeMatch = trimmed.match(/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z|Before incident|Incident [Ss]tart|During [Ii]ncident|After resolution|Incident Detection|T[+-]?\d+|T\+X|T-Current|T\+Current):\s*(.+)$/);
                                      if (timeMatch) {
                                        const time = timeMatch[1];
                                        const description = timeMatch[2];

                                        // Determine severity based on content
                                        let severity = 'low';
                                        if (description.toLowerCase().includes('critical') || description.toLowerCase().includes('outage') || description.toLowerCase().includes('failure')) {
                                          severity = 'critical';
                                        } else if (description.toLowerCase().includes('error') || description.toLowerCase().includes('spike') || description.toLowerCase().includes('alert')) {
                                          severity = 'high';
                                        } else if (description.toLowerCase().includes('degraded') || description.toLowerCase().includes('investigation')) {
                                          severity = 'medium';
                                        }

                                        events.push({ time, description, severity });
                                      }
                                    });

                                    if (events.length > 0) {
                                      return events.map((event, index) => (
                                        <div key={index} className="flex items-start gap-4">
                                          <div className="flex-shrink-0">
                                            <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm font-semibold">
                                              {index + 1}
                                            </div>
                                          </div>
                                          <div className="flex-1">
                                            <div className="flex items-center justify-between mb-1">
                                              <h4 className="text-white font-semibold">{event.time}</h4>
                                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                                event.severity === 'critical' ? 'bg-red-600 text-white' :
                                                event.severity === 'high' ? 'bg-orange-600 text-white' :
                                                event.severity === 'medium' ? 'bg-yellow-600 text-white' :
                                                'bg-green-600 text-white'
                                              }`}>
                                                {event.severity}
                                              </span>
                                            </div>
                                            <p className="text-gray-300 text-sm">{event.description}</p>
                                          </div>
                                        </div>
                                      ));
                                    }
                                  }

                                  // Fallback to markdown rendering
                                  return (
                                    <div className="text-gray-300">
                                      {renderMarkdown(timeline)}
                                    </div>
                                  );
                                })()}
                              </div>
                            </div>
                          )}

                          {/* Hypotheses */}
                          {hasInvestigation && investigation.hypotheses && Array.isArray(investigation.hypotheses) && investigation.hypotheses.length > 0 && (
                            <div className="bg-white/5 border border-white/10 rounded-lg p-6">
                              <h3 className="text-lg font-semibold mb-4 text-white">Investigation Hypotheses</h3>
                              <div className="space-y-4">
                                {investigation.hypotheses.map((hypothesis: any, index: number) => (
                                  <div key={index} className="bg-[#1A1A1A] border border-gray-700 rounded-lg p-4">
                                    <div className="flex items-center justify-between mb-2">
                                      <h4 className="text-white font-medium">Hypothesis {index + 1}</h4>
                                      <span className="px-2 py-1 rounded text-xs font-medium bg-blue-600 text-white">
                                        {(hypothesis.confidence * 100).toFixed(0)}% confidence
                                      </span>
                                    </div>
                                    <p className="text-gray-300 text-sm mb-3">{hypothesis.root_cause}</p>
                                    {hypothesis.evidence && (
                                      <div className="space-y-2">
                                        {hypothesis.evidence.supporting && hypothesis.evidence.supporting.length > 0 && (
                                          <div>
                                            <span className="text-xs font-semibold text-green-400">Supporting Evidence:</span>
                                            <ul className="list-disc list-inside mt-1 space-y-1">
                                              {hypothesis.evidence.supporting.map((item: string, idx: number) => (
                                                <li key={idx} className="text-xs text-gray-400">{item}</li>
                                              ))}
                                            </ul>
                                          </div>
                                        )}
                                        {hypothesis.evidence.contradicting && hypothesis.evidence.contradicting.length > 0 && (
                                          <div>
                                            <span className="text-xs font-semibold text-red-400">Contradicting Evidence:</span>
                                            <ul className="list-disc list-inside mt-1 space-y-1">
                                              {hypothesis.evidence.contradicting.map((item: string, idx: number) => (
                                                <li key={idx} className="text-xs text-gray-400">{item}</li>
                                              ))}
                                            </ul>
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Recommended Actions */}
                          {hasInvestigation && investigation.recommended_actions && Array.isArray(investigation.recommended_actions) && investigation.recommended_actions.length > 0 && (
                            <div className="bg-white/5 border border-white/10 rounded-lg p-6">
                              <h3 className="text-lg font-semibold mb-4 text-white">Recommended Actions</h3>
                              <div className="space-y-3">
                                {investigation.recommended_actions.map((action: string, index: number) => (
                                  <div key={index} className="flex items-start gap-3 p-3 bg-[#1A1A1A] border border-gray-700 rounded-lg">
                                    <div className="flex-shrink-0 mt-0.5">
                                      <div className="w-5 h-5 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-semibold">
                                        {index + 1}
                                      </div>
                                    </div>
                                    <p className="text-gray-300 text-sm">{action}</p>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Evidence - Old format */}
                          {!hasInvestigation && parsedAnalysisResult?.evidence && (
                            <div className="bg-white/5 border border-white/10 rounded-lg p-6">
                              <h3 className="text-lg font-semibold mb-4 text-white">Evidence</h3>
                              <div className="text-gray-300">
                                {renderMarkdown(parsedAnalysisResult.evidence)}
                              </div>
                            </div>
                          )}

                          {/* Similar Incidents */}
                          <div className="bg-white/5 border border-white/10 rounded-lg p-6">
                            <h3 className="text-lg font-semibold mb-4 text-white">Similar Past Incidents</h3>
                            {(() => {
                              const similarIncidents = hasInvestigation
                                ? (investigation.similar_incidents || [])
                                : (alertAnalysis.similar_incidents || []);

                              if (Array.isArray(similarIncidents) && similarIncidents.length > 0) {
                                return (
                                  <div className="space-y-3">
                                    {similarIncidents.map((incident: any, index: number) => (
                                      <a
                                        key={index}
                                        href={incident.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-start gap-3 p-4 bg-[#1A1A1A] hover:bg-[#252525] border border-gray-700 hover:border-gray-600 rounded-lg transition-colors group"
                                      >
                                        <div className="flex-shrink-0 mt-1">
                                          <ExternalLink className="w-4 h-4 text-blue-400 group-hover:text-blue-300" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                          <div className="flex items-center justify-between gap-2 mb-1">
                                            <h4 className="text-white font-medium group-hover:text-blue-300 transition-colors truncate">
                                              {incident.title || incident.id}
                                            </h4>
                                            <div className="flex items-center gap-2 flex-shrink-0">
                                              {incident.date && (
                                                <span className="text-xs text-[#8b949e] whitespace-nowrap">
                                                  {new Date(incident.date).toLocaleDateString()}
                                                </span>
                                              )}
                                            </div>
                                          </div>
                                          {incident.summary && (
                                            <p className="text-sm text-gray-400 line-clamp-2">{incident.summary}</p>
                                          )}
                                        </div>
                                      </a>
                                    ))}
                                  </div>
                                );
                              }
                              return (
                                <div className="text-center py-8 text-gray-400">
                                  <p>No similar past incidents found</p>
                                </div>
                              );
                            })()}
                          </div>
                        </>
                      );
                    })()}
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="text-gray-400 mb-4">
                  <svg className="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <p className="text-gray-400">No analysis available for this alert</p>
              </div>
            )}
          </div>

          {/* Timeline Section - Only show if no analysis or no timeline in analysis */}
          {(() => {
            const parsedAnalysisResult = alertAnalysis?.analysis_result ?
              (typeof alertAnalysis.analysis_result === 'string' ?
                (() => { try { return JSON.parse(alertAnalysis.analysis_result); } catch { return null; } })() :
                alertAnalysis.analysis_result) :
              null;

            const hasTimeline = parsedAnalysisResult?.investigation?.timeline &&
                               Array.isArray(parsedAnalysisResult.investigation.timeline) &&
                               parsedAnalysisResult.investigation.timeline.length > 0;
            const hasOldTimeline = parsedAnalysisResult?.timeline;

            if (!alertAnalysis || !hasTimeline && !hasOldTimeline) {
              return (
                <div className="bg-[#222222] rounded-lg p-6">
                  <h2 className="text-xl font-semibold text-white mb-6">Timeline</h2>
                  <div className="text-center py-8 text-[#8b949e]">
                    Timeline data not available for this alert
                  </div>
                </div>
              );
            }
            return null;
          })()}
        </div>
      </div>
    </div>
  );
};
